import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, AutocompleteItem, AutocompleteSection } from '@nextui-org/react';
import { useGetCredentialsListQuery, type Credential } from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';
import { WebAgentCredentialModal } from './WebAgentCredentialModal';

/** A step value referencing a stored secret. The id is kept, never the secret itself. */
const CREDENTIAL_REF_RE = /^\$\{\s*credential\.([^}\s]+)\s*\}$/;
const credentialRef = (credentialId: string) => `\${credential.${credentialId}}`;
const credentialIdOf = (value?: string | number) => CREDENTIAL_REF_RE.exec(String(value ?? ''))?.[1] ?? '';

/** True when a stored value points at a credential rather than being a literal the user typed. */
export const isCredentialValue = (value?: string | number) => Boolean(credentialIdOf(value));

/** What the editor shows for a value: a credential's name, or the literal itself. */
const displayFor = (credentials: Credential[], value: string) => {
  const id = credentialIdOf(value);
  if (!id) return value;
  return credentials.find((c) => c.credential_id === id)?.name ?? id;
};

const CUSTOM_KEY = '__create_credential__';
/** Options keep their full text on one line; the list scrolls horizontally instead of truncating. */
const ITEM_CLASSES = { base: 'max-w-none', title: 'whitespace-nowrap' };

export interface WebAgentValueFieldProps {
  readonly value: string;
  readonly placeholder?: string;
  readonly isReadOnly?: boolean;
  readonly onChange: (value: string) => void;
}

/**
 * The secret a step sends to the page. Secrets are never typed here: they are picked from the stored
 * credentials and persisted as `${credential.<id>}`, so the editor only ever shows the credential's
 * name and the real value stays server-side until the agent runs. Literals and run values have their
 * own value types in the step row, so typing here only filters the list. The step row owns the Value
 * label, so this renders as an unlabelled full-width control.
 */
export function WebAgentValueField({
  value, placeholder, isReadOnly, onChange,
}: WebAgentValueFieldProps) {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const { data: credentials, refetch } = useGetCredentialsListQuery(undefined, { skip: isReadOnly });

  const list: Credential[] = useMemo(() => (Array.isArray(credentials) ? credentials : []), [credentials]);

  // A saved step holds the credential id; the editor shows its name.
  const display = useMemo(() => displayFor(list, value), [list, value]);

  // The text is held locally rather than derived on every render. Feeding a derived value straight back
  // into a controlled combobox makes react-aria re-fire onInputChange and loop; it would also overwrite
  // the stored reference with the credential's name the moment the list resolves.
  const [text, setText] = useState(display);
  const lastEmitted = useRef(value);

  useEffect(() => {
    // Adopt the display when the value changed outside this field (JSON view, assistant applying steps)
    // or when a credential's name has just resolved. Typing is unaffected: it keeps value === lastEmitted
    // and a credential reference always displays as its name, so setText is then a no-op.
    if (value !== lastEmitted.current || isCredentialValue(value)) {
      lastEmitted.current = value;
      setText(display);
    }
  }, [display, value]);

  const emit = (next: string) => {
    lastEmitted.current = next;
    onChange(next);
  };

  const handleSelection = (key: React.Key | null) => {
    if (key == null) return;
    const picked = String(key);
    if (picked === CUSTOM_KEY) {
      setModalOpen(true);
      return;
    }
    setText(displayFor(list, picked));
    emit(picked);
  };

  /** Select the credential just created, once the refreshed list contains it. Matched on the code,
   *  which the user sets and the API keeps unique — names are not. */
  const handleCreated = async (code: string) => {
    const { data: refreshed } = await refetch();
    const created = (Array.isArray(refreshed) ? refreshed : []).find((c) => c.code === code);
    if (!created) return;
    setText(created.name);
    emit(credentialRef(created.credential_id));
  };

  return (
    <>
      <Autocomplete
        size="sm"
        aria-label="step_value"
        className="w-full"
        placeholder={placeholder}
        allowsCustomValue
        isReadOnly={isReadOnly}
        isDisabled={isReadOnly}
        // Options are shown in full: the popover sizes to its content and the list scrolls sideways
        // rather than ellipsising a long credential name or selector.
        popoverProps={{ classNames: { content: 'w-auto min-w-[240px] max-w-[min(90vw,42rem)]' }, placement: 'bottom-start' }}
        listboxProps={{ classNames: { list: 'max-w-none overflow-x-auto' } }}
        inputValue={text}
        onSelectionChange={handleSelection}
        // Typing narrows the list rather than writing the value: only a credential (or a newly created
        // one) may be stored here, so half-typed text is discarded when the field loses focus. There is
        // deliberately no selectedKey either — react-aria syncing it back into the input is what made
        // this field loop.
        onInputChange={setText}
        onBlur={() => setText(display)}
      >
        <AutocompleteSection key="new" title={t('Create')}>
          <AutocompleteItem key={CUSTOM_KEY} classNames={ITEM_CLASSES} textValue={t('Custom — new credential')}>
            {t('Custom — new credential')}
          </AutocompleteItem>
        </AutocompleteSection>
        <AutocompleteSection key="credentials" title={t('Credentials')}>
          {list.map((credential) => (
            <AutocompleteItem key={credentialRef(credential.credential_id)} classNames={ITEM_CLASSES} textValue={credential.name}>
              {credential.name}
            </AutocompleteItem>
          ))}
        </AutocompleteSection>
      </Autocomplete>

      {isModalOpen && (
        <WebAgentCredentialModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}

export default WebAgentValueField;

